import { useCallback, useEffect, useMemo, useState } from 'react';
import { timeframes } from '../../utils/helpers';

// Selector for which timeframes appear inside each level cell (up to 3 per
// level). A timeframe belongs to at most one level's list, so a tile can never
// be claimed by two levels. The side/wave columns are kept as a live monitor
// while choosing. Laid out as an aligned table so the 15s -> 8d progression and
// the wave column stay scannable top to bottom.

const LEVELS = [
  { key: 'tactical', label: 'T' },
  { key: 'optimal', label: 'M' },
  { key: 'strategic', label: 'S' },
];

const MAX_PER_LEVEL = 3;

// A/B/C are presets and ZERO is not a timeframe; neither can be selected.
export const SELECTABLE_TIMEFRAMES = timeframes.filter((tf) => tf !== 'ZERO');

// Unit changes are the landmarks operators navigate by.
const unitOf = (timeframe) => timeframe.replace(/[0-9]/g, '');

export const buildBlacklistStorageKey = (asset, username) =>
  `tos-panel:${asset?.broker}:${asset?.account || username}:${asset?.symbol}:tf-blacklist`;

const readBlacklist = (storageKey) => {
  if (typeof window === 'undefined') return [];
  try {
    const raw = window.localStorage.getItem(storageKey);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch (error) {
    console.error('Failed to read timeframe blacklist from localStorage:', error);
    return [];
  }
};

const TimeframeMatrix = ({
  timeframeStates = {},
  binding = {},
  asset = {},
  username = '',
  // (level, nextList) — the level's new display list after a toggle.
  onSetDisplayList = () => { },
  defaultCollapsed = false,
}) => {
  const storageKey = buildBlacklistStorageKey(asset, username);
  const [blacklist, setBlacklist] = useState(() => readBlacklist(storageKey));
  const [showHidden, setShowHidden] = useState(false);
  const [collapsed, setCollapsed] = useState(defaultCollapsed);

  // Asset switches reuse the component, so the list must follow the asset.
  useEffect(() => {
    setBlacklist(readBlacklist(storageKey));
    setShowHidden(false);
  }, [storageKey]);

  const persistBlacklist = useCallback((next) => {
    setBlacklist(next);
    if (typeof window === 'undefined') return;
    try {
      window.localStorage.setItem(storageKey, JSON.stringify(next));
    } catch (error) {
      console.error('Failed to persist timeframe blacklist to localStorage:', error);
    }
  }, [storageKey]);

  const displayLists = useMemo(
    () => Object.fromEntries(
      LEVELS.map(({ key }) => [key, binding[`${key}_display_timeframes`] || []])
    ),
    [binding]
  );

  // Which level (if any) already shows this timeframe. Uniqueness guarantees one.
  const ownerOf = useCallback(
    (timeframe) => LEVELS.find(({ key }) => displayLists[key].includes(timeframe)) || null,
    [displayLists]
  );

  // A displayed timeframe stays visible even if blacklisted — hiding it would
  // strand a selection the operator cannot see or remove.
  const isHidden = useCallback(
    (timeframe) => blacklist.includes(timeframe) && !ownerOf(timeframe),
    [blacklist, ownerOf]
  );

  const visibleTimeframes = SELECTABLE_TIMEFRAMES.filter((tf) => !isHidden(tf));
  const hiddenTimeframes = SELECTABLE_TIMEFRAMES.filter(isHidden);
  const emptyLevels = LEVELS.filter(({ key }) => displayLists[key].length === 0);

  const handleToggle = (levelKey, timeframe) => {
    const current = displayLists[levelKey];
    if (current.includes(timeframe)) {
      onSetDisplayList(levelKey, current.filter((tf) => tf !== timeframe));
    } else {
      onSetDisplayList(levelKey, [...current, timeframe]);
    }
  };

  const renderRow = (timeframe, index) => {
    const state = timeframeStates[timeframe] || {};
    const side = (state.side || '').toLowerCase();
    const wave = state.wave || '--';
    const owner = ownerOf(timeframe);
    const previous = visibleTimeframes[index - 1];
    const startsNewUnit = previous && unitOf(previous) !== unitOf(timeframe);

    return (
      <div
        key={timeframe}
        data-timeframe={timeframe}
        className={`flex items-center gap-2 px-1.5 h-6 text-[11px] rounded ${
          startsNewUnit ? 'mt-1 border-t border-gray-200 pt-px' : ''
        } ${owner ? 'bg-indigo-50' : 'hover:bg-gray-100'}`}
      >
        <span className={`w-9 shrink-0 font-mono ${owner ? 'font-bold text-indigo-700' : 'text-gray-600'}`}>
          {timeframe}
        </span>

        <span
          className={`w-8 shrink-0 text-center font-semibold ${
            side === 'buy' ? 'text-emerald-600'
              : side === 'sell' ? 'text-red-500'
                : 'text-gray-300'
          }`}
          title={side || 'no side yet'}
        >
          {side ? side.toUpperCase().slice(0, 1) : '·'}
        </span>

        <span
          className={`w-7 shrink-0 font-mono ${wave === '--' ? 'text-gray-300' : 'text-gray-700'}`}
          title="current wave"
        >
          {wave}
        </span>

        <span className="flex gap-0.5">
          {LEVELS.map(({ key, label }) => {
            const selectedHere = displayLists[key].includes(timeframe);
            const ownedElsewhere = owner && owner.key !== key;
            const levelFull = displayLists[key].length >= MAX_PER_LEVEL;
            const disabled = ownedElsewhere || (!selectedHere && levelFull);
            return (
              <button
                key={key}
                disabled={disabled}
                onClick={() => handleToggle(key, timeframe)}
                title={
                  selectedHere
                    ? `Remove ${timeframe} from ${label}`
                    : ownedElsewhere
                      ? `${timeframe} is already shown on ${owner.label}`
                      : levelFull
                        ? `${label} already shows ${MAX_PER_LEVEL} timeframes`
                        : `Show ${timeframe} on ${label}`
                }
                className={`w-5 h-4 rounded text-[10px] font-bold transition-colors ${
                  selectedHere
                    ? 'bg-indigo-600 text-white'
                    : disabled
                      ? 'bg-gray-50 text-gray-200 cursor-not-allowed'
                      : 'bg-gray-100 text-gray-300 hover:bg-indigo-100 hover:text-indigo-600'
                }`}
              >
                {label}
              </button>
            );
          })}
        </span>

        <button
          onClick={() => persistBlacklist([...blacklist, timeframe])}
          disabled={!!owner}
          title={owner ? 'Remove from the level before hiding' : `Hide ${timeframe}`}
          className={`w-4 shrink-0 text-[11px] leading-none transition-colors ${
            owner ? 'cursor-not-allowed text-transparent' : 'text-gray-300 hover:text-gray-600'
          }`}
        >
          ×
        </button>
      </div>
    );
  };

  return (
    <div className="mt-2 rounded-xl border border-gray-200 bg-white p-2">
      {/* The whole header toggles, not just the label — a thin strip of text is
          a needlessly small target for something clicked this often. */}
      <button
        type="button"
        onClick={() => setCollapsed(!collapsed)}
        title={collapsed ? 'Show the timeframe selector' : 'Hide the timeframe selector'}
        className="mb-1 flex w-full items-center justify-between rounded px-0.5 py-0.5 text-left hover:bg-gray-100"
      >
        <span className="flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-gray-400">
          <span className="text-[9px]">{collapsed ? '▸' : '▾'}</span>
          Timeframes
        </span>
        {/* A level with no timeframes selected trades nothing; kept visible even
            while collapsed so the gap is not hidden. */}
        {emptyLevels.length > 0 && (
          <span
            className="rounded bg-red-100 px-1.5 py-0.5 text-[10px] font-semibold text-red-600"
            title="Levels with no timeframe selected"
          >
            empty: {emptyLevels.map(({ label }) => label).join(' ')}
          </span>
        )}
      </button>

      {!collapsed && (
        <>
          {/* Column header — the anchor that makes the rows scannable */}
          <div className="flex items-center gap-2 px-1.5 pb-1 text-[9px] font-semibold uppercase tracking-wide text-gray-300">
            <span className="w-9 shrink-0">tf</span>
            <span className="w-8 shrink-0 text-center">side</span>
            <span className="w-7 shrink-0">wave</span>
            <span className="w-[62px] shrink-0 text-center">show on</span>
          </div>

          <div className="max-h-64 overflow-y-auto">
            {visibleTimeframes.map(renderRow)}
          </div>
        </>
      )}

      {!collapsed && hiddenTimeframes.length > 0 && (
        <div className="mt-1.5 border-t border-gray-200 pt-1.5">
          <button
            onClick={() => setShowHidden(!showHidden)}
            className="text-[10px] font-medium text-gray-400 hover:text-gray-600"
          >
            {showHidden ? '▾' : '▸'} hidden ({hiddenTimeframes.length})
          </button>
          {showHidden && (
            <div className="mt-1 flex flex-wrap gap-1">
              {hiddenTimeframes.map((timeframe) => (
                <button
                  key={timeframe}
                  onClick={() => persistBlacklist(blacklist.filter((tf) => tf !== timeframe))}
                  title={`Show ${timeframe}`}
                  className="rounded border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] text-gray-400 hover:border-gray-300 hover:text-gray-600"
                >
                  + {timeframe}
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeframeMatrix;
